// ========================================================
// Buscador global — diseño D (2026-10-26)
// Como el anterior (todo centrado, buscador grande, filtros y una tarjeta por resultado)
// con el diseño nuevo: cada resultado abre directamente su ficha.
// Estilos: assets/css/views/buscar.css
// ========================================================
const GlobalSearchView = {
    currentCategory: 'all',
    searchQuery: '',
    _leads: new Map(),

    CATEGORIES: [
        {key: 'all', label: 'Todo'},
        {key: 'student', label: 'Alumnos', type: 'Alumno'},
        {key: 'professor', label: 'Profesores', type: 'Profesor'},
        {key: 'lead', label: 'Contactos', type: 'Contacto'},
        {key: 'formation', label: 'Formaciones', type: 'Formación'},
        {key: 'subject', label: 'Asignaturas', type: 'Asignatura'}
    ],
    // Nombres antiguos de las categorías (el desplegable de la cabecera aún puede usarlos).
    _LEGACY: {students: 'student', professors: 'professor', leads: 'lead', formations: 'formation', subjects: 'subject'},

    render() {
        if (typeof AuthManager !== 'undefined' && AuthManager.isStudent()) {
            return `
                <div class="dnr-proposal gs-view">
                    <div class="gs-denied" role="alert">
                        <h2>Acceso restringido</h2>
                        <p>El buscador es solo para el personal del centro.</p>
                    </div>
                </div>`;
        }
        this.currentCategory = this._LEGACY[this.currentCategory] || this.currentCategory;
        return `
            <div class="dnr-proposal gs-view">
                <header class="gs-hero">
                    <h1 class="gs-title"><span>SICAF</span> <span class="gs-title-accent">· Buscar</span></h1>
                    <p class="gs-sub">Alumnos, profesores, contactos, formaciones y asignaturas del centro</p>
                </header>
                <div class="gs-box">
                    <label for="google-search-input" class="sr-only">Buscar</label>
                    <svg class="gs-box-icon" aria-hidden="true" width="22" height="22" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round"><circle cx="11" cy="11" r="7"></circle><path d="m20 20-3.5-3.5"></path></svg>
                    <input type="search" id="google-search-input" class="gs-input" data-search-clear="off" autocomplete="off"
                        placeholder="Nombre, correo, teléfono, formación o asignatura…" value="${Utils.sanitizeHTML(this.searchQuery)}">
                    <button type="button" id="google-search-clear-btn" class="gs-clear" aria-label="Borrar la búsqueda" ${this.searchQuery ? '' : 'hidden'}>×</button>
                </div>
                <div id="google-search-results-area">${this._renderResults()}</div>
            </div>`;
    },

    setCategory(category) {
        this.currentCategory = this._LEGACY[category] || category;
        this._refresh();
    },

    _refresh() {
        const area = document.getElementById('google-search-results-area');
        if (area) SafeHTML.set(area, this._renderResults());
    },

    attachEventListeners() {
        const input = document.getElementById('google-search-input');
        const clearBtn = document.getElementById('google-search-clear-btn');
        if (input) {
            input.focus();
            const len = input.value.length;
            input.setSelectionRange(len, len);
            input.addEventListener('input', Utils.debounce((e) => {
                this.searchQuery = e.target.value.trim();
                if (clearBtn) clearBtn.hidden = !this.searchQuery;
                this._refresh();
            }, 250));
            // Intro abre el primer resultado.
            input.addEventListener('keydown', (e) => {
                if (e.key !== 'Enter') return;
                const first = document.querySelector('#google-search-results-area .gs-row');
                if (first) { e.preventDefault(); first.click(); }
            });
        }
        if (clearBtn) {
            clearBtn.addEventListener('click', () => {
                this.searchQuery = '';
                if (input) { input.value = ''; input.focus(); }
                clearBtn.hidden = true;
                this._refresh();
            });
        }
    },

    /** Todos los resultados de la búsqueda, de todos los tipos. */
    _collect() {
        const q = Utils.normalizeString(this.searchQuery);
        const has = (...parts) => Utils.normalizeString(parts.filter(Boolean).join(' ')).includes(q);
        const results = [];
        const formations = DataManager.getFormations() || [];
        const formationName = id => formations.find(f => f.id === id)?.name || '';

        (DataManager.getAll(DataManager.KEYS.STUDENTS) || []).forEach(s => {
            const name = `${s.firstName || s.name || ''} ${s.lastName || ''}`.trim();
            if (!has(name, s.email, s.phone, s.nif)) return;
            const studies = (s.formations || []).map(formationName).filter(Boolean).join(', ');
            results.push({type: 'student', id: s.id, title: name, sub: s.email || 'Sin correo',
                meta: [studies, s.phone ? `Tlf ${s.phone}` : '', s.active === false ? 'Baja' : ''].filter(Boolean).join(' · '),
                open: 'Abrir ficha'});
        });

        (DataManager.getProfessors() || []).forEach(p => {
            const name = `${p.firstName || ''} ${p.lastName || ''}`.trim();
            if (!has(name, p.email, p.phone, p.specialty)) return;
            results.push({type: 'professor', id: p.id, title: name, sub: p.email || 'Sin correo',
                meta: [p.specialty, p.phone ? `Tlf ${p.phone}` : ''].filter(Boolean).join(' · '), open: 'Abrir ficha'});
        });

        // Los contactos se buscan en el servidor (diez como mucho): el navegador ya no los tiene todos.
        const query = this.searchQuery;
        const leads = typeof LeadService !== 'undefined' ? LeadService.searchCached(query, () => {
            if (this.searchQuery === query) this._refresh();
        }) : [];
        this._leadsLoading = leads === null;
        (leads || []).forEach(l => {
            this._leads.set(String(l.id), l);
            const status = typeof LeadsView !== 'undefined' && LeadsView.STATUS?.[l.status] ? LeadsView.STATUS[l.status][0] : (l.status || '');
            results.push({type: 'lead', id: l.id, title: l.nombre || 'Sin nombre', sub: l.email || 'Sin correo',
                meta: [l.interes ? `Interés: ${l.interes}` : '', status].filter(Boolean).join(' · '), open: 'Abrir contacto'});
        });

        const types = typeof FormationTypeService !== 'undefined' ? FormationTypeService.getTypes() : [];
        formations.forEach(f => {
            const typeName = types.find(t => t.id === f.type)?.name || f.type || '';
            if (!has(f.name, typeName)) return;
            results.push({type: 'formation', id: f.id, title: f.name, sub: typeName || 'Formación',
                meta: f.duration ? `${f.duration} ${f.duration == 1 ? 'año' : 'años'}` : '', open: 'Ver formaciones'});
        });

        (DataManager.getSubjects() || []).forEach(sub => {
            if (!has(sub.name)) return;
            const prof = sub.professorId ? DataManager.getProfessor(sub.professorId) : null;
            results.push({type: 'subject', id: sub.id, title: sub.name,
                sub: prof ? `Profesor: ${prof.firstName} ${prof.lastName}` : 'Sin profesor',
                meta: formationName(sub.formationId), open: 'Plan de estudios'});
        });
        return results;
    },

    /** El nombre con lo buscado resaltado (sin tildes ni mayúsculas, como la búsqueda). */
    _highlight(text) {
        const q = Utils.normalizeString(this.searchQuery);
        const chars = Array.from(String(text || ''));
        const flat = chars.map(ch => Utils.normalizeString(ch));
        const joined = flat.join('');
        const at = q ? joined.indexOf(q) : -1;
        if (at < 0) return Utils.sanitizeHTML(text);
        let pos = 0, from = -1, to = chars.length;
        flat.forEach((part, i) => {
            if (from < 0 && pos + part.length > at) from = i;
            if (to === chars.length && pos >= at + q.length) to = i;
            pos += part.length;
        });
        const esc = s => Utils.sanitizeHTML(s);
        return `${esc(chars.slice(0, from).join(''))}<mark>${esc(chars.slice(from, to).join(''))}</mark>${esc(chars.slice(to).join(''))}`;
    },

    _initials(title) {
        return String(title || '').split(/[\s(]+/).filter(Boolean).slice(0, 2).map(w => w[0]).join('').toUpperCase() || '·';
    },

    _renderChips(all) {
        return `<div class="gs-chips" role="group" aria-label="Filtrar por tipo">${this.CATEGORIES.map(c => {
            const n = c.key === 'all' ? all.length : all.filter(r => r.type === c.key).length;
            const on = this.currentCategory === c.key;
            return `<button type="button" class="gs-chip ${on ? 'is-on' : ''}" data-kind="${c.key}" aria-pressed="${on}"
                ${UIEvents.attr('click', function(event, captured) { GlobalSearchView.setCategory(String(captured[0])); }, [c.key])}>
                ${c.key === 'all' ? '' : '<span class="gs-dot" aria-hidden="true"></span>'}${c.label} <span class="gs-chip-n">${n}</span></button>`;
        }).join('')}</div>`;
    },

    _renderResults() {
        if (!this.searchQuery) {
            return `${this._renderChips([])}
                <div class="gs-empty">
                    <h2>Empieza a escribir</h2>
                    <p>Busca por nombre, correo, teléfono, NIF, formación o asignatura.</p>
                </div>`;
        }
        this.currentCategory = this._LEGACY[this.currentCategory] || this.currentCategory;
        const all = this._collect();
        const shown = this.currentCategory === 'all' ? all : all.filter(r => r.type === this.currentCategory);
        const quoted = `«${Utils.sanitizeHTML(this.searchQuery)}»`;
        if (shown.length === 0) {
            return `${this._renderChips(all)}
                <div class="gs-empty">
                    <h2>${this._leadsLoading ? 'Buscando…' : `Sin resultados para ${quoted}`}</h2>
                    <p>${this._leadsLoading ? 'Consultando los contactos.' : 'Prueba con otras palabras o cambia el tipo.'}</p>
                </div>`;
        }
        const typeLabel = Object.fromEntries(this.CATEGORIES.map(c => [c.key, c.type]));
        return `${this._renderChips(all)}
            <p class="gs-count" aria-live="polite">${shown.length} ${shown.length === 1 ? 'resultado' : 'resultados'} para ${quoted}${this._leadsLoading ? ' · buscando contactos…' : ''}</p>
            <div class="gs-list">
                ${shown.map(r => `
                    <button type="button" class="gs-row" data-kind="${r.type}"
                        ${UIEvents.attr('click', function(event, captured) { GlobalSearchView.open(String(captured[0]), String(captured[1])); }, [r.type, r.id])}>
                        <span class="gs-av" aria-hidden="true">${Utils.sanitizeHTML(this._initials(r.title))}</span>
                        <span class="gs-text">
                            <span class="gs-name-line"><strong class="gs-name">${this._highlight(r.title)}</strong><span class="gs-type">${typeLabel[r.type]}</span></span>
                            <span class="gs-line">${Utils.sanitizeHTML(r.sub)}</span>
                            ${r.meta ? `<span class="gs-meta">${Utils.sanitizeHTML(r.meta)}</span>` : ''}
                        </span>
                        <span class="gs-open">${r.open}</span>
                        <span class="gs-go" aria-hidden="true">›</span>
                    </button>`).join('')}
            </div>`;
    },

    /** Abre la ficha del resultado (o su sección si no tiene ficha propia). */
    open(type, id) {
        if (type === 'student') {
            const s = (DataManager.getAll(DataManager.KEYS.STUDENTS) || []).find(x => String(x.id) === id);
            App.navigate(s ? Utils.studentProfileUrl(s) : `alumno/${id}`);
        } else if (type === 'professor') {
            App.navigate(`profesor/${id}`);
        } else if (type === 'lead') {
            const lead = this._leads.get(id);
            if (typeof LeadsView !== 'undefined' && lead) {
                // Contactos abre el año del contacto, buscado por su correo y con su ficha a la vista.
                LeadsView.currentTab = 'all';
                LeadsView.currentFormationFilter = 'all';
                LeadsView.currentSearch = lead.email || lead.nombre || '';
                LeadsView.currentYearFilter = LeadsView.getLeadTargetYearStart(lead);
                LeadsView.currentPage = 1;
                LeadsView.openLeadId = lead.id;
            }
            App.navigate('leads');
        } else if (type === 'formation') {
            App.navigate('formaciones');
        } else if (type === 'subject') {
            App.navigate('plan-estudios');
        }
    }
};

window.GlobalSearchView = GlobalSearchView;
